import React from 'react';
import PropTypes from 'prop-types';
import './additionalInfo.scss';

// Shows professional background (applicant-provided) and neutral spending
// patterns. Deliberately shows "not collected" for social media, and has
// NO health/medical line — we don't process health data.
const AdditionalInfo = ({ application }) => {
  const riskAssessment = application.riskAssessment || {};
  const publicProfileAssessment = application.publicProfileAssessment || null;
  const patterns = riskAssessment?.spendingPatterns;
  const medicalPatterns = riskAssessment?.medicalPatterns;
  const educationPatterns = riskAssessment?.educationPatterns;
  const insurancePatterns = riskAssessment?.insurancePatterns;
  const underwritingNotes = (riskAssessment?.underwritingNotes || []).filter(Boolean);
  const consistentCategories = riskAssessment?.consistentExpenseCategories || [];
  const loanPayments = riskAssessment?.insights?.loanPayments || [];
  const recurringPayments = riskAssessment?.insights?.recurringPayments || [];

  console.log('riskAssessment', riskAssessment);

  // Build the list of detected neutral spending categories.
  const detectedPatterns = [];
  if (patterns?.grocery) detectedPatterns.push('Regular grocery spending');
  if (patterns?.fuel) detectedPatterns.push('Regular fuel / transport spending');
  if (patterns?.utilities) detectedPatterns.push('Regular utility payments');
  if (patterns?.subscriptions) detectedPatterns.push('Regular subscription payments');
  if (medicalPatterns?.recurring) {
    detectedPatterns.push(
      `Recurring healthcare-related transactions observed (${medicalPatterns.frequency})`
    );
  }

  if (educationPatterns?.recurring) {
    detectedPatterns.push(`Recurring education-related spending (${educationPatterns.frequency})`);
  }

  if (insurancePatterns?.recurring) {
    detectedPatterns.push(`Recurring insurance payments (${insurancePatterns.frequency})`);
  }

  return (
    <section className="addl-info">
      <header className="addl-info__head">
        <span className="addl-info__tag">Additional Information</span>
      </header>

      <div className="addl-info__body">
        {/* Professional background — applicant-provided */}
        <div className="addl-info__block">
          <span className="addl-info__block-label">Professional background</span>
          <div className="addl-info__rows">
            <div className="addl-info__row">
              <span className="addl-info__row-label">Employer</span>
              <span className="addl-info__row-value">
                {application.employerName || 'Not provided'}
              </span>
            </div>
            <div className="addl-info__row">
              <span className="addl-info__row-label">Employment type</span>
              <span className="addl-info__row-value">
                {application.employmentType || 'Not provided'}
              </span>
            </div>
            <div className="addl-info__row">
              <span className="addl-info__row-label">Years at employer</span>
              <span className="addl-info__row-value">
                {application.yearsAtEmployer != null
                  ? `${application.yearsAtEmployer} years`
                  : 'Not provided'}
              </span>
            </div>
          </div>
        </div>

        {/* Spending patterns — neutral categories only */}
        <div className="addl-info__block">
          <span className="addl-info__block-label">Spending patterns</span>
          {detectedPatterns.length > 0 ? (
            <ul className="addl-info__patterns">
              {detectedPatterns.map((p, i) => (
                <li key={i} className="addl-info__pattern">
                  {p}
                </li>
              ))}
              {consistentCategories.length > 0 && (
                <li className="addl-info__pattern">
                  Consistent categories: {consistentCategories.join(', ')}
                </li>
              )}
            </ul>
          ) : (
            <span className="addl-info__empty">No recurring spending patterns identified</span>
          )}
        </div>

        {/* Existing loan / EMI obligations */}
        <div className="addl-info__block">
          <span className="addl-info__block-label">Existing EMI / Loan Obligations</span>

          {loanPayments.length > 0 ? (
            <>
              <div className="addl-info__rows">
                <div className="addl-info__row">
                  <span className="addl-info__row-label">Monthly EMI detected</span>

                  <span className="addl-info__row-value">
                    ₹
                    {Number(riskAssessment?.insights?.existingEmiTotal || 0).toLocaleString(
                      'en-IN'
                    )}
                  </span>
                </div>

                <div className="addl-info__row">
                  <span className="addl-info__row-label">Lenders detected</span>

                  <span className="addl-info__row-value">{loanPayments.length}</span>
                </div>
              </div>

              <div className="addl-info__loan-list">
                {loanPayments.map((loan, index) => (
                  <div key={`${loan.lender}-${index}`} className="addl-info__loan-item">
                    <div className="addl-info__loan-main">
                      <span className="addl-info__loan-lender">{loan.lender}</span>

                      <span className="addl-info__loan-frequency">
                        {loan.frequency || 'unknown'}
                      </span>
                    </div>

                    <span className="addl-info__loan-amount">
                      ₹{Number(loan.amount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <span className="addl-info__empty">
              No existing loan or EMI repayments identified from the bank statement
            </span>
          )}
        </div>

        {/* Other recurring financial obligations */}
        <div className="addl-info__block">
          <span className="addl-info__block-label">Other Recurring Payments</span>

          {recurringPayments.length > 0 ? (
            <div className="addl-info__recurring-list">
              {recurringPayments
                .filter((payment) => payment.category !== 'loan')
                .map((payment, index) => (
                  <div key={`${payment.merchant}-${index}`} className="addl-info__recurring-item">
                    <div className="addl-info__recurring-main">
                      <div className="addl-info__recurring-name">
                        {payment.merchant || 'Unknown provider'}
                      </div>

                      <div className="addl-info__recurring-meta">
                        <span>{payment.category || 'other'}</span>
                        <span>•</span>
                        <span>{payment.frequency || 'unknown'}</span>
                      </div>
                    </div>

                    <span className="addl-info__recurring-amount">
                      ₹{Number(payment.amount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <span className="addl-info__empty">
              No other recurring financial obligations identified
            </span>
          )}
        </div>

        <div className="addl-info__block">
          <span className="addl-info__block-label">Underwriting Notes</span>

          {underwritingNotes.length > 0 ? (
            <ul className="addl-info__patterns">
              {underwritingNotes.map((note, index) => (
                <li key={index} className="addl-info__pattern">
                  {note}
                </li>
              ))}
            </ul>
          ) : (
            <span className="addl-info__empty">No underwriting notes generated</span>
          )}
        </div>

        {/* Social media — honestly stated as not collected */}
        {/* Public profile verification */}
        <div className="addl-info__block">
          <span className="addl-info__block-label">Public Profile Verification</span>

          {!publicProfileAssessment ? (
            <span className="addl-info__empty">
              Public profile verification has not been performed
            </span>
          ) : (
            <>
              <div className="addl-info__rows">
                <div className="addl-info__row">
                  <span className="addl-info__row-label">Profile</span>
                  <span className="addl-info__row-value">
                    {publicProfileAssessment.publicProfileFound
                      ? `${publicProfileAssessment.profileType || 'Public profile'} found`
                      : 'Not found'}
                  </span>
                </div>

                <div className="addl-info__row">
                  <span className="addl-info__row-label">Employment information</span>
                  <span className="addl-info__row-value">
                    {publicProfileAssessment.employmentInfoFound ? 'Found' : 'Not found'}
                  </span>
                </div>

                <div className="addl-info__row">
                  <span className="addl-info__row-label">Education information</span>
                  <span className="addl-info__row-value">
                    {publicProfileAssessment.educationInfoFound ? 'Found' : 'Not found'}
                  </span>
                </div>
              </div>

              {publicProfileAssessment.publicProfileFound && (
                <>
                  {/* Employment comparison */}
                  <div className="addl-info__profile-comparison">
                    <span className="addl-info__row-label">Employment verification</span>

                    <div className="addl-info__rows">
                      <div className="addl-info__row">
                        <span className="addl-info__row-label">Application</span>
                        <span className="addl-info__row-value">
                          {publicProfileAssessment.employment?.applicationEmployer ||
                            'Not provided'}
                        </span>
                      </div>

                      <div className="addl-info__row">
                        <span className="addl-info__row-label">Public profile</span>
                        <span className="addl-info__row-value">
                          {publicProfileAssessment.employment?.profileEmployer || 'Not available'}
                        </span>
                      </div>

                      <div className="addl-info__row">
                        <span className="addl-info__row-label">Match</span>
                        <span
                          className={`addl-info__match ${
                            publicProfileAssessment.employment?.match === true
                              ? 'addl-info__match--match'
                              : publicProfileAssessment.employment?.match === false
                                ? 'addl-info__match--mismatch'
                                : 'addl-info__match--unknown'
                          }`}>
                          {publicProfileAssessment.employment?.match === true
                            ? 'Match'
                            : publicProfileAssessment.employment?.match === false
                              ? 'Mismatch'
                              : 'Not available'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Education comparison */}
                  <div className="addl-info__profile-comparison">
                    <span className="addl-info__row-label">Education verification</span>

                    <div className="addl-info__rows">
                      <div className="addl-info__row">
                        <span className="addl-info__row-label">Application</span>
                        <span className="addl-info__row-value">
                          {publicProfileAssessment.education?.applicationInstitution ||
                            'Not provided'}
                        </span>
                      </div>

                      <div className="addl-info__row">
                        <span className="addl-info__row-label">Public profile</span>
                        <span className="addl-info__row-value">
                          {publicProfileAssessment.education?.profileInstitution || 'Not available'}
                        </span>
                      </div>

                      <div className="addl-info__row">
                        <span className="addl-info__row-label">Match</span>
                        <span className="addl-info__row-value">
                          {publicProfileAssessment.education?.match === true
                            ? 'Match'
                            : publicProfileAssessment.education?.match === false
                              ? 'Mismatch'
                              : 'Not available'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {publicProfileAssessment.profileUrl && (
                    <div className="addl-info__row">
                      <span className="addl-info__row-label">Profile URL</span>

                      <span className="addl-info__row-value">
                        <a
                          href={publicProfileAssessment.profileUrl}
                          target="_blank"
                          rel="noopener noreferrer">
                          View profile
                        </a>
                      </span>
                    </div>
                  )}
                </>
              )}

              {publicProfileAssessment.notes?.length > 0 && (
                <div className="addl-info__profile-notes">
                  <span className="addl-info__row-label">Notes</span>

                  <ul className="addl-info__patterns">
                    {publicProfileAssessment.notes.filter(Boolean).map((note, index) => (
                      <li key={index} className="addl-info__pattern">
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

AdditionalInfo.propTypes = {
  application: PropTypes.shape({
    employerName: PropTypes.string,
    employmentType: PropTypes.string,
    yearsAtEmployer: PropTypes.number,
    publicProfileAssessment: PropTypes.object,
    riskAssessment: PropTypes.shape({
      insights: PropTypes.shape({
        spendingPatterns: PropTypes.object
      }),
      spendingPatterns: PropTypes.shape({
        grocery: PropTypes.bool,
        fuel: PropTypes.bool,
        utilities: PropTypes.bool,
        subscriptions: PropTypes.bool,
        consistentOverall: PropTypes.bool
      }),

      medicalPatterns: PropTypes.shape({
        recurring: PropTypes.bool,
        frequency: PropTypes.string
      }),

      educationPatterns: PropTypes.shape({
        recurring: PropTypes.bool,
        frequency: PropTypes.string
      }),

      insurancePatterns: PropTypes.shape({
        recurring: PropTypes.bool,
        frequency: PropTypes.string
      }),

      underwritingNotes: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired
};

export default AdditionalInfo;
